import { HiddenField, InlineInputField, InlineSelect } from "components/custom/programs/fields";
import { Form } from "react-bootstrap";
import { useWatch } from "react-hook-form";
import { useProgramContext } from "./ProgramContext";
import { useAppSelector } from "redux/hooks";
import { MARKETPLACE_CURRENCY_DENOMINATION } from "utils/marketplaceConstants";
import useAds from "hooks/useAds";
import { useFetch } from "hooks/api";

const PriceForm = ({ action, fieldName, control }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const days = useWatch({ name: `${fieldName}.values.1`, control });

  let adjustOptions = [
    {
      value: "adjust_fixed",
      label: MARKETPLACE_CURRENCY_DENOMINATION[marketPlace.marketPlace],
    },
    { value: "adjust_percent", label: "%" },
  ];

  if (action === "SET_BID") {
    adjustOptions = [
      {
        value: "adjust_fixed",
        label: MARKETPLACE_CURRENCY_DENOMINATION[marketPlace.marketPlace],
      },
    ];
  }

  if (action === "SET_BID_CPC") {
    let isDaysPlural = true;
    try {
      isDaysPlural = days && parseInt(days) > 1;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      isDaysPlural = true;
    }
    return (
      <>
        <span className="px-3">of last</span>
        <InlineInputField fieldName={`${fieldName}.values.1`} control={control} rules={{ required: true }} />
        <span className="px-3"> {isDaysPlural ? "days" : "day"}, adjusted by</span>
        <InlineSelect
          options={adjustOptions}
          fieldName={`${fieldName}.operator`}
          control={control}
          rules={{ required: true }}
        />
        <InlineInputField fieldName={`${fieldName}.values.0`} control={control} rules={{ required: true }} />
      </>
    );
  }

  return (
    <>
      {action !== "SET_PRICE" && (
        <InlineSelect
          options={adjustOptions}
          fieldName={`${fieldName}.operator`}
          control={control}
          rules={{ required: true }}
        />
      )}
      <InlineInputField fieldName={`${fieldName}.values.0`} control={control} rules={{ required: true }} />
    </>
  );
};

const HarvestKeywordAction = ({ fieldName, control }) => {
  const { ads } = useAds();

  const selectedAds = useWatch({
    name: `targets`,
    control,
  });

  const selectedAdId = useWatch({
    name: `${fieldName}.values.0`,
    control,
  });

  const { data: adGroupData } = useFetch(
    ["adGroups", selectedAdId],
    "/api/ad_groups",
    {
      advertisement: selectedAdId,
    },
    {
      enabled: !!selectedAdId,
      select: (res) => res.data ?? [],
    },
  );

  const adGroupOptions =
    adGroupData?.map((adGroup) => {
      return { value: adGroup.ad_group_id, label: adGroup.ad_group_name };
    }) ?? [];

  const options =
    ads?.filter((ad) => selectedAds?.includes(ad.id)).map((ad) => ({ value: ad.id, label: ad.name })) ?? [];
  const selectedAd = ads?.find((ad) => ad.id === selectedAdId);
  return (
    <>
      <InlineSelect
        title={"select media plan"}
        fieldName={`${fieldName}.values.0`}
        control={control}
        rules={{ required: true }}
        options={options}
      />
      {selectedAd?.ad_source === "Imported" && adGroupOptions.length > 0 && (
        <>
          ad group
          <InlineSelect
            title="select ad group"
            shouldUnregister
            fieldName={`${fieldName}.values.1`}
            control={control}
            rules={{ required: true }}
            options={adGroupOptions}
          />
        </>
      )}
    </>
  );
};

const ActionForm = ({ fieldName, control, initialAction }) => {
  const { engine } = useProgramContext();
  const actionOptions = {
    pricing: [
      { value: "SET_PRICE", label: "Set price" },
      { value: "INCREASE_PRICE", label: "Increase price" },
      { value: "LOWER_PRICE", label: "Decrease price" },
    ],
    bidding:
      initialAction ?
        [
          { value: "SET_BID", label: "Set bid" },
          { value: "USE_ALGO", label: "Use optimal bid" },
        ]
      : [
          { value: "SET_BID", label: "Set bid" },
          { value: "INCREASE_BID", label: "Increase bid" },
          { value: "LOWER_BID", label: "Decrease bid" },
          { value: "USE_ALGO", label: "Use optimal bid" },
          { value: "SET_BID_CPC", label: "Set bid based on average CPC" },
          { value: "DO_NOTHING", label: "Keep current bid" },
          // { value: "PAUSE", label: "Pause keyword or target" },
        ],
    budget: [
      { value: "SET_BUDGET", label: "Set budget" },
      { value: "INCREASE_BUDGET", label: "Increase budget" },
      { value: "LOWER_BUDGET", label: "Decrease budget" },
      /// { value: "DISABLE_BIDDING", label: "Pause bidding" },
      // { value: "ENABLE_BIDDING", label: "Enable bidding" },
      { value: "INCREASE_BID_MULTIPLIER", label: "Increase bid multiplier" },
      { value: "LOWER_BID_MULTIPLIER", label: "Decrease bid multiplier" },
      { value: "SET_BID_MULTIPLIER", label: "Set bid multiplier" },
      { value: "DO_NOTHING", label: "Do nothing" },
    ],
    harvesting: [
      { value: "HARVEST_QUERY", label: "Harvest Query Exact" },
      { value: "HARVEST_QUERY_PHRASE", label: "Harvest Query Phrase" },
      { value: "NEGATE_QUERY_EXACT", label: "Negate Query Exact" },
      { value: "NEGATE_QUERY_PHRASE", label: "Negate Query Phrase" },
    ],
    lineitem_bidding:
      initialAction ?
        [{ value: "SET_BASE_BID", label: "Set base bid" }]
      : [
          { value: "SET_BASE_BID", label: "Set base bid" },
          { value: "INCREASE_BASE_BID", label: "Increase base bid" },
          { value: "LOWER_BASE_BID", label: "Decrease base bid" },
          { value: "DO_NOTHING", label: "Keep current base bid" },
        ],
  };
  const action = useWatch({
    control,
    name: `${fieldName}.function`,
  });

  return (
    <Form.Group>
      <div className="d-flex align-items-center flex-wrap fs-standard">
        <InlineSelect
          options={actionOptions[engine]}
          fieldName={`${fieldName}.function`}
          control={control}
          rules={{ required: true }}
        />
        {action.includes("BID_MULTIPLIER") && (
          <>
            <InlineSelect
              fieldName={`${fieldName}.values.1`}
              control={control}
              rules={{ required: true }}
              defaultValue="PLACEMENT_TOP"
              options={[
                { value: "PLACEMENT_TOP", label: "Top of Search" },
                { value: "PLACEMENT_PRODUCT_PAGE", label: "Detail Page" },
                { value: "PLACEMENT_REST_OF_SEARCH", label: "Rest of Search" },
              ]}
            />
            <HiddenField fieldName={`${fieldName}.operator`} control={control} defaultValue="adjust_fixed" />
            <InlineInputField fieldName={`${fieldName}.values.0`} control={control} rules={{ required: true }} /> %
          </>
        )}
        {action.includes("HARVEST_QUERY") && (
          <>
            in to <HarvestKeywordAction fieldName={fieldName} control={control} />
          </>
        )}
        {action.includes("NEGATE_QUERY") && (
          <>
            in{" "}
            <InlineSelect
              fieldName={`${fieldName}.values.0`}
              control={control}
              rules={{ required: true }}
              options={[
                { value: "source", label: "Source Campaign" },
                { value: "all", label: "All Source Campaigns" },
              ]}
            />
          </>
        )}
        {![
          "RESET_ACC",
          "NEGATE",
          "PAUSE",
          "REVERT_TO_ALGO",
          "DO_NOTHING",
          "x",
          "USE_ALGO",
          "INCREASE_BID_MULTIPLIER",
          "LOWER_BID_MULTIPLIER",
          "SET_BID_MULTIPLIER",
          "HARVEST_QUERY",
          "HARVEST_QUERY_PHRASE",
          "NEGATE_QUERY_EXACT",
          "NEGATE_QUERY_PHRASE",
        ].includes(action) && (
          <PriceForm
            action={action}
            fieldName={`${fieldName}`}
            control={control}
            rules={{ required: true }}
            actionOptions={actionOptions[engine]}
          />
        )}
      </div>
    </Form.Group>
  );
};

export default ActionForm;
